import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import {getRomanNumber, niceUserName} from "utils/functions";
import {useAuth} from "../../../hooks/useAuth";
import CustomDataTable from "../../../shared/tables/CustomDataTable";
import StyledLink from "../../../shared/ui/LinkStyled";
import GenericButton from "../../../shared/ui/GenericButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {getClassroomsTableColumns} from "./_tableColumns";
import {APIGetClassrooms} from "../api/classrooms";
import {APIArchiveClassroom, APIRestoreClassroom} from "../api/classroom";
import useDataTable from "../../../hooks/useDataTable";
import {IClassroomPage} from "../../../shared/types/Classroom";
import {ITeacher} from "../../../shared/types/Users";

type ClassroomsProps = {
    isArchived?: boolean;
};

type ClassroomAction = "archive" | "restore";

const classroomActionConfig = {
    archive: {
        api: APIArchiveClassroom,
        title: "Ești sigur că vrei să arhivezi această clasă?",
        destinationLabel: "Clase arhivate",
        checkboxLabel: "Arhivare elevi",
        buttonYes: "Arhivează",
        successMessage: "va fi arhivată.",
    },
    restore: {
        api: APIRestoreClassroom,
        title: "Ești sigur că vrei să dezarhivezi această clasă?",
        destinationLabel: "Clase active",
        checkboxLabel: "Dezarhivare elevi",
        buttonYes: "Dezarhivează",
        successMessage: "va fi dezarhivată.",
    },
};

const Classrooms = ({isArchived = false}: ClassroomsProps) => {
    const {update, user, tenant} = useAuth();
    const navigate = useNavigate();
    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "name", undefined, "");

    const studentsStatusRef = useRef(true);

    const isSchool = tenant.type === "school";
    const isPrincipal = user.position === "principal";
    const isTeacher = user.position === "teacher";

    const tableColumns = useMemo(
        () => getClassroomsTableColumns(user.position, tenant.type),
        [user.position, tenant.type],
    );

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const renderUserLink = (classroom: IClassroomPage) => (
        <StyledLink to={`/clase/${classroom.id}`}>{classroom.name}</StyledLink>
    );

    const renderTeachers = (teachers?: ITeacher[]) => {
        if (!teachers) return null;

        return (teachers.map(teacher => (
            isPrincipal ? (
                <StyledLink
                    key={teacher.id}
                    to={`/profesori/${teacher.id}`}
                    sx={{display: "inline-block", mr: 1}}
                >
                    {niceUserName(teacher.first_name, teacher.last_name, teacher.email,)}
                </StyledLink>
            ) : (
                <Box key={teacher.id} display="inline-block" mr={1}>
                    {niceUserName(teacher.first_name, teacher.last_name, teacher.email,)}
                </Box>
            )
        )) )
    }

    const getActions = (classroom: IClassroomPage) => (
        <Box display="flex" justifyContent="end">
            <Box mr={1}>
                <GenericButton
                    type="edit-small"
                    onClick={() => {
                        editClassroomHandler(classroom.id);
                    }}
                />
            </Box>
            <Box>
                {isArchived ? (
                    <GenericButton type="restore" onClick={() => confirmClassroomAction("restore", classroom)}/>
                ) : (
                    <GenericButton type="archive" onClick={() => confirmClassroomAction("archive", classroom)}/>
                )}
            </Box>
        </Box>
    )

    const mapClassroomToRow = useCallback((classroom: IClassroomPage) => {
        return {
            name: renderUserLink(classroom),
            grade: getRomanNumber(classroom.grade),
            students_count: classroom.students_count,
            ...(isSchool && {teachers: renderTeachers(classroom.teachers),}),
            ...((isSchool && isTeacher) ? null : {actions: getActions(classroom)}),
        }
    }, []);

    const getClassrooms = useCallback(async (currentPage = pageRef.current) => {
        try {
            const response = await APIGetClassrooms({
                page: `?page=${currentPage + 1}`,
                sort: orderBy,
                order: order,
                search: submittedQuery,
                isArchived: isArchived,
            });

            if(!response.data.success) {
                update("error", {show: true, code: "A002"});
                return;
            }

            const pageData = response.data.data;

            if (pageData.data.length === 0 && currentPage > 0) {
                const previousPage = currentPage - 1;

                setPage(previousPage);
                await getClassrooms(previousPage);
                return;
            }

            setTableData(pageData.data.map((classroom: any) => mapClassroomToRow(classroom)));
            setTotalRows(pageData.total);
            setPerPage(pageData.per_page);
        } catch {
            update("error", {show: true, code: "A001"});
        } finally {
            setLoading(false);
        }

    }, [order, submittedQuery, isArchived, setTableData, mapClassroomToRow, setTotalRows, setPerPage, update, setLoading, setPage]);

    useEffect(() => {
        setLoading(true);
        getClassrooms();
    }, [page, orderBy, order, submittedQuery]);

    const editClassroomHandler = useCallback((id: number) => {
        navigate(`/clase/${id}/editare`);
    }, [navigate]);

    const StudentsStatusCheckbox = ({label}: { label: string; }) => {
        const [checked, setChecked] = useState(true);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextChecked = event.target.checked;

            setChecked(nextChecked);
            studentsStatusRef.current = nextChecked;
        };

        return (
            <FormControlLabel
                control={
                    <Checkbox
                        checked={checked}
                        onChange={handleChange}
                    />
                }
                label={label}
            />
        );
    };

    const updateClassroomArchivedStatus = (
        action: ClassroomAction,
        classroomId: number,
        callback: () => void,
        status: boolean,
    ) => {
        update("loading", {show: true, modal: true});

        classroomActionConfig[action].api({
            id: classroomId,
            status,
        })
            .then((response) => {
                if (response.data.success) {
                    getClassrooms(pageRef.current);
                } else {
                    update("error", {show: true, code: "A002"});
                }
                callback();
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => update("loading", {show: false}));
    };

    const confirmClassroomAction = (
        action: ClassroomAction,
        classroom: IClassroomPage,
    ) => {
        const config = classroomActionConfig[action];

        studentsStatusRef.current = true;

        update("confirm", {
            show: true,
            closable: true,
            icon: "fat box-archive",
            title: config.title,
            message: (
                <>
                    Clasa <b>({getRomanNumber(classroom.grade)}) {classroom.name}</b>{" "}
                    {config.successMessage}
                    Veți regăsi această clasă în secțiunea <b>{config.destinationLabel}</b>.
                    <br/>
                    <StudentsStatusCheckbox label={config.checkboxLabel}/>
                </>
            ),
            returnFunction: (callback: () => void) => {
                updateClassroomArchivedStatus(
                    action,
                    classroom.id,
                    callback,
                    studentsStatusRef.current,
                );
            },
            buttonYes: config.buttonYes,
            buttonNo: "Închide",
        });
    };

    return (
        <CustomDataTable
            loading={loading}
            columns={tableColumns}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={true}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            searchValue={query}
            onClearSearch={handleClearSearch}
        />
    )
}

export default Classrooms;